import { Injectable } from '@angular/core';
import { UzorService } from '../encryption/uzor.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';

export interface AppMeta {
  services: string[];
  prefix: string;
  routes: { path: string; view: string | null }[];
  ngrok: string;
}

export interface GeneratedApp {
  name: string;           // e.g. 'auth', 'smtp', 'card'
  port: number;           // e.g. 1959, 1960, 1961
  baseUrl: string;        // https://100.85.124.80:1959
  apiUrl: string;         // https://100.85.124.80:1959/api/transaction
  dailyKey: string;       // today's SHA256 hash
}

@Injectable({
  providedIn: 'root'
})
export class AppService {
  constructor(private uzs: UzorService) {}
 /** Metadata that comes from AppView */
  private _meta$ = new BehaviorSubject<AppMeta | null>(null);
  meta$ = this._meta$.asObservable();

  /** Cookies received from the SPA */
  private _cookies$ = new BehaviorSubject<Record<string, string>>({});
  cookies$ = this._cookies$.asObservable();

  /** Current SPA route (e.g. "/web/app/mail") */
  private _currentPath$ = new BehaviorSubject<string>('');
  currentPath$ = this._currentPath$.asObservable();

 
  /** -----------------------------------------------------------------
   * Save the catalogue that AppView posts.
   * ----------------------------------------------------------------- */
  receiveMeta(meta: AppMeta) {
    this._meta$.next(meta);
  }

  /** -----------------------------------------------------------------
   * Save cookies that come from the SPA.
   * ----------------------------------------------------------------- */
  receiveCookies(cookies: Record<string, string>) {
    this._cookies$.next(cookies);
  }


  /** -----------------------------------------------------------------
   * Helper – give a *snapshot* of the latest metadata (synchronous).
   * ----------------------------------------------------------------- */
  getMetaSnapshot(): AppMeta | null {
    return this._meta$.value;
  }

  /**
   * Generate full app info including today's daily key
   * @param name App name (case insensitive) e.g. 'auth', 'SMTP', 'card'
   * @param port Port number the app is running on
   */
  generate(name: string, port: number): GeneratedApp | null {
    if (!name || typeof name !== 'string' || !port || typeof port !== 'number' || port <= 0) {
      console.warn('Invalid app name or port');
      return null;
    }

    const appName = name.trim();

    const baseUrl = `https://${environment.serverAddress}:${port}`;
    const apiUrl = `${baseUrl}/api/transaction`;

    // Used for daily key generation
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');

    // Pattern: name + full base url + date
    const pattern = `${appName}${baseUrl}${year}${month}${day}`;
    const dailyKey = this.uzs.hashSha256(pattern);

    return {
      name: appName,
      port,
      baseUrl,
      apiUrl,
      dailyKey
    };
  }

  /**
   * Quick helper: get only today's daily key for given app/port
   */
  getDailyKey(name: string, port: number): string | null {
    const app = this.generate(name, port);
    return app?.dailyKey ?? null;
  }

  /**
   * Get info about the CURRENT running application
   * (using environment.app + assuming current port is known / passed / from config)
   */
  getCurrentApp(): GeneratedApp | null {
    const currentAppName = (environment.app || 'unknown').trim();
    return this.generate(currentAppName, environment.port);
  }
}
